import { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { isNil } from 'lodash';
import { RecordOf, List } from 'immutable';

// COMPONENTS
import RadioButton from 'components/RadioButtons/RadioButton.subcomponent';

// STYLES
import { Label } from 'styles/modals';
import { LargeSelector, LargeSelectorLabel, LargeSelectorChildren, RadioWrapper } from 'styles/form';
import { RadioRound, RadioGroup } from './RadioButtons.styles';

const MaybeTitle = (props) => {
  const { title } = props;

  if (isNil(props.title)) {
    return null;
  }

  if (typeof title === 'string') {
    return (
      <Label>
        <FormattedMessage id={title} />
      </Label>
    );
  }

  return title;
};

class RadioButtons<
  T extends {
    id: number;
    renderedValue: any;
  }
> extends PureComponent<
  {
    active: boolean;
    selectedItemId: number;
    title: string;
    onSelect: (item: RecordOf<T>) => void;
    items: List<RecordOf<T>>;
  },
  { selected: number }
> {
  static defaultProps = {
    active: true,
    selectedItemId: null,
    title: null,
  };

  state = {
    selected: this.props.selectedItemId,
  };

  componentDidUpdate = (prevProps) => {
    if (!isNil(this.props.selectedItemId) && prevProps.selectedItemId !== this.props.selectedItemId) {
      this.setState({ selected: this.props.selectedItemId });
    }
  };

  handleClick = (item) => () => {
    if (!this.props.active) {
      return;
    }

    this.setState({ selected: item.id });

    this.props.onSelect(item);
  };

  renderButtons = () =>
    this.props.items.map((item) => {
      const selected = this.state.selected === item.get('id');

      return (
        <RadioButton
          key={item.get('id')}
          active={this.props.active}
          renderedValue={item.get('renderedValue')}
          // @ts-expect-error TS(2769): No overload matches this call.
          selected={selected}
          onClick={this.handleClick(item)}
        />
      );
    });

  render() {
    const { items, active, onSelect, selectedItemId, title, ...props } = this.props;

    return (
      <RadioWrapper {...props}>
        <MaybeTitle title={this.props.title} />
        <RadioGroup>{this.renderButtons()}</RadioGroup>
      </RadioWrapper>
    );
  }
}

interface Props<N extends string> {
  children: React.ReactNode;
  label: string | React.ReactNode;
  name: N;
  justifyContent?: string;
  alignItems?: string;
  onClick: (name: N) => void;
  selected: string;
  thin?: boolean;
}

export const LargeRadioSelector = <N extends string>(props: Props<N>) => {
  const { children, label, name, onClick, selected, thin = false } = props;

  const isSelected = selected === name;

  return (
    <LargeSelector
      selected={isSelected}
      onClick={() => onClick(name)}
      flexDirection="row"
      alignItems="start"
      thin={thin}
    >
      <RadioRound selected={isSelected} onClick={() => onClick(name)} data-testid="radio-button" tabIndex={0}>
        <div />
      </RadioRound>
      <LargeSelectorChildren>
        <LargeSelectorLabel>{label}</LargeSelectorLabel>
        {children && <div>{children}</div>}
      </LargeSelectorChildren>
    </LargeSelector>
  );
};

export default RadioButtons;
